import React from 'react'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Tag, Tooltip } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {
  ESellerDisableReason,
  ESellerInactiveReason,
  ESellerManualDisableBy,
  ISellerManualDisable
} from 'models/seller'
import { STOCK_TERMS_HINT } from 'components/sellers/StockConfirmationStatus/utils/stockConfirmation'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import { getSellerDisableReasons, ISellerDisableState } from 'shared/utils/sellerDisableReasons'
import styles from './SellerDisableStatus.module.scss'

interface ISellerDisableStatusProps {
  seller: ISellerDisableState & {
    disabled_for_cancels_until?: string | null
    manual_disable?: ISellerManualDisable | null
    inactive_reasons?: ESellerInactiveReason[]
  }
  hideActive?: boolean
}

interface IDisableReasonInfo {
  label: string
  color: string
  about: string
  consequences: string
}

const HIDDEN_CONSEQUENCES =
  'Магазин и его товары не видны покупателям: карточки товаров открываются с ошибкой 404, Яндекс получает сигнал, что страницы удалены.'

export const SELLER_DISABLE_REASONS: Record<ESellerDisableReason, IDisableReasonInfo> = {
  [ESellerDisableReason.MANUAL]: {
    label: 'Отключен вручную',
    color: 'error',
    about: 'Включён переключатель «Скрыть товары продавца»',
    consequences: HIDDEN_CONSEQUENCES
  },
  [ESellerDisableReason.CANCELS]: {
    label: 'Отключен за отмены',
    color: 'error',
    about: '4-я отмена заказа продавцом за 30 дней — магазин отключается на 14 дней.',
    consequences: `${HIDDEN_CONSEQUENCES} До конца срока выключить переключатель «Скрыть товары продавца» может только администратор площадки. После срока магазин включится сам, а если у продавца нет рабочего способа доставки — останется отключён с причиной «Профиль не заполнен».`
  },
  [ESellerDisableReason.STOCK_OUTDATED]: {
    label: 'Отключен за остатки',
    color: 'warning',
    about: `Продавец не обновил остатки в срок. ${STOCK_TERMS_HINT}`,
    consequences: 'Карточки товаров открываются, но купить товары нельзя, в каталоге и поиске их нет. Магазин включится сам, как только продавец обновит или подтвердит остатки.'
  },
  [ESellerDisableReason.PROFILE_INCOMPLETE]: {
    label: 'Профиль не заполнен',
    color: 'error',
    about: 'Срок отключения за отмены истёк, но у продавца нет рабочего способа доставки.',
    consequences: `${HIDDEN_CONSEQUENCES} Магазин включится сам, как только продавец настроит доставку.`
  }
}

const MANUAL_DISABLE_BY: Record<ESellerManualDisableBy, string> = {
  [ESellerManualDisableBy.ADMIN]: 'администратором',
  [ESellerManualDisableBy.SELLER]: 'продавцом'
}

const getManualAbout = (about: string, manualDisable?: ISellerManualDisable | null) =>
  `${about} — ${manualDisable ? MANUAL_DISABLE_BY[manualDisable.by] : 'кем, не записано'}.`

// Для ячейки таблицы: тап мимо тегов открывает подсказку первого из них. Работает
// только на сенсорном экране — мышью подсказку открывает наведение
export const openStatusHint = (event: React.MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement
  if (!event.currentTarget.contains(target) || target.closest(`.${styles.tag}`)) return
  event.currentTarget.querySelector<HTMLElement>(`.${styles.tag}`)?.click()
}

type TSellerConditionsState = ISellerDisableState & {
  inactive_reasons?: ESellerInactiveReason[]
}

type TConditionMark = 'met' | 'failed'

interface IActivityCondition {
  label: string
  check: (seller: TSellerConditionsState) => TConditionMark
}

const markIf = (isMet: boolean): TConditionMark => (isMet ? 'met' : 'failed')

const ACTIVITY_CONDITIONS: IActivityCondition[] = [
  {
    label: 'магазин не отключён',
    check: (seller) => markIf(!getSellerDisableReasons(seller).length)
  },
  {
    label: 'есть хотя бы один опубликованный товар',
    check: (seller) =>
      markIf(!seller.inactive_reasons?.includes(ESellerInactiveReason.NO_PRODUCTS))
  },
  {
    label: 'заходили в кабинет хотя бы раз за последние 30 дней',
    check: (seller) =>
      markIf(!seller.inactive_reasons?.includes(ESellerInactiveReason.NO_VISITS))
  }
]

const CONDITION_ICONS: Record<TConditionMark, React.ReactNode> = {
  met: <CheckOutlined className={styles.conditionMet} />,
  failed: <CloseOutlined className={styles.conditionFailed} />
}

// С seller у каждого условия отметка, без него — просто список
export const SellerActivityConditions: React.FC<{ seller?: TSellerConditionsState }> = ({
  seller
}) => (
  <ul className={classNames(styles.conditions, seller && styles.conditionsChecked)}>
    {ACTIVITY_CONDITIONS.map(({ label, check }) => {
      const mark = seller && check(seller)
      return (
        <li key={label} className={styles.condition}>
          {mark && CONDITION_ICONS[mark]}
          <span>{label}</span>
        </li>
      )
    })}
  </ul>
)

const ACTIVE_CONSEQUENCES = 'Магазин не отключён: товары видны покупателям и продаются.'

const INACTIVE_CONSEQUENCES =
  'нет — магазин не отключён и работает как обычно. Неактивность — только оценка работы продавца.'

export const SellerDisableStatus: React.FC<ISellerDisableStatusProps> = ({
  seller,
  hideActive
}) => {
  const { isTouch } = useBreakpoint()
  const hintTrigger = isTouch ? 'click' : 'hover'
  const reasons = getSellerDisableReasons(seller)

  if (!reasons.length) {
    if (hideActive) return null
    const inactiveReasons = seller.inactive_reasons ?? []
    const isActive = !inactiveReasons.length
    const label = isActive ? 'Активный' : 'Неактивный'
    return (
      <span>
        <Tooltip
          trigger={hintTrigger}
          title={
            <div className={styles.hint}>
              <div className={styles.hintTitle}>{label}</div>
              <div>
                {isActive
                  ? 'Выполнены все условия активности:'
                  : 'Выполнены не все условия активности:'}
              </div>
              <SellerActivityConditions seller={seller} />
              <div>
                {isActive
                  ? ACTIVE_CONSEQUENCES
                  : <><span className={styles.hintTitle}>Последствия:</span> {INACTIVE_CONSEQUENCES}</>}
              </div>
            </div>
          }
        >
          <Tag color={isActive ? 'success' : 'default'} className={styles.tag}>{label}</Tag>
        </Tooltip>
      </span>
    )
  }

  const until = seller.disabled_for_cancels_until

  return (
    <span className={styles.tags}>
      {reasons.map((reason) => {
        const { label, color, about, consequences } = SELLER_DISABLE_REASONS[reason]
        const isManual = reason === ESellerDisableReason.MANUAL
        return (
          <Tooltip
            key={reason}
            trigger={hintTrigger}
            title={
              <div className={styles.hint}>
                <div className={styles.hintTitle}>
                  {label}
                  {reason === ESellerDisableReason.CANCELS && until &&
                    ` до ${dayjs(until).format('DD.MM.YYYY HH:mm')}`}
                </div>
                <div>{isManual ? getManualAbout(about, seller.manual_disable) : about}</div>
                <div>
                  <span className={styles.hintTitle}>Последствия:</span> {consequences}
                </div>
                {isManual && (
                  <div className={styles.hintComment}>
                    <span className={styles.hintTitle}>Комментарий:</span>{' '}
                    {seller.manual_disable?.comment ?? 'не указан'}
                  </div>
                )}
              </div>
            }
          >
            <Tag color={color} className={styles.tag}>{label}</Tag>
          </Tooltip>
        )
      })}
    </span>
  )
}
